<template>
  <div v-show="otrasCapas" class="geoproceso-selector-capa">
    <div v-if="isLoadingLayers" class="loading-status">
      <font-awesome-icon icon="spinner" spin /> Cargando capas...
    </div>

    <div class="selector-wrapper">
      <label class="selector-label">Capa de referencia:</label>
      <div class="selector-container">
        <select 
          :value="value"
          @change="onLayerChange"
          class="form-control custom-select"
        >
          <option value="">[Seleccionar una capa]</option>
          <optgroup v-for="(category, index) in filteredCategories" :key="index" :label="category.name">
            <option v-for="layer in category.layers" :key="layer.id" :value="layer.id">
              {{ layer.name }}
            </option>
          </optgroup>
        </select>
      </div>
    </div>

    <div v-if="value" class="selection-hint" role="status">
      <span class="selection-hint-icon" aria-hidden="true">▭</span>
      <span>Arrastra sobre el mapa para dibujar un rectángulo de selección.</span>
    </div>

  </div>
</template>

<script>
import { LayerService } from '@/services/layers/layerService';
import { IMPORT_CATEGORY_ID, IMPORT_CATEGORY_NAME } from '@/services/layers/importedLayerRegistry';
import { mapService } from '@/services/map/mapService';
import { geoprocessingSelectionHandler } from '@/components/mapTools/geoprocessings/utils/GeoprocessingSelectionHandler';

export default {
  name: 'GeoprocesoSelectorCapa',
  props: {
    value: String,           // ID of the selected layer (capaOtrasSeleccionada)
    otrasCapas: Boolean,
    tipoInfluencia: String,
    distancias: {
      type: Array,
      default: () => []
    },
    dissolve: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      filteredCategories: [],
      isLoadingLayers: false,
      temporaryLayerId: '',
      layerLoadSequence: 0,
    };
  },
  mounted() {
    this.updateLayerFilter();
  },
  beforeDestroy() {
    this.removeTemporaryLayer(this.value);
    geoprocessingSelectionHandler.clearHighlight();
    const map = mapService.get();
    if (map) geoprocessingSelectionHandler.stopSelection(map);
  },
  computed: {
    importedLayers() {
      return this.$store.getters.getImportedLayers || [];
    },
  },
  watch: {
    tipoInfluencia() {
      this.updateLayerFilter();
    },
    otrasCapas(val) {
      if (val) this.updateLayerFilter();
    },
    importedLayers: {
      deep: true,
      handler() {
        if (this.otrasCapas) this.updateLayerFilter();
      },
    },
    distancias(newVal) {
      geoprocessingSelectionHandler.updateOptions({
        distancias: this.getOrderedDistances(newVal)
      });
    },
    dissolve(newVal) {
      geoprocessingSelectionHandler.updateOptions({ dissolve: newVal });
    }
  },
  methods: {
    getOrderedDistances(values) {
      if (!Array.isArray(values) || values.length === 0) return [];
      const distances = values.map(value => Number(value));
      const areValid = distances.every((value, index) => (
        Number.isFinite(value) &&
        value > 0 &&
        (index === 0 || value > distances[index - 1])
      ));
      return areValid ? distances : [];
    },
    async updateLayerFilter() {
      if (!this.otrasCapas) return;
      this.isLoadingLayers = true;
      try {
        const storedCategories = this.$store.getters.getLayers || [];
        const allCategories = this.importedLayers.length
          ? [...storedCategories, { id: IMPORT_CATEGORY_ID, name: IMPORT_CATEGORY_NAME, layers: this.importedLayers }]
          : storedCategories;
        this.filteredCategories = await LayerService.filterLayersByInfluence(allCategories, this.tipoInfluencia);
        if (this.value && !this.filteredCategories.some(category => category.layers.some(layer => layer.id == this.value))) {
          this.removeTemporaryLayer(this.value);
          this.$emit('input', '');
          geoprocessingSelectionHandler.clearHighlight();
        }
      } finally {
        this.isLoadingLayers = false;
      }
    },
    removeTemporaryLayer(layerId) {
      if (layerId && this.temporaryLayerId === layerId) {
        LayerService.removeLayerByID(layerId);
        this.temporaryLayerId = '';
      }
    },
    async onLayerChange(event) {
      const layerId = event.target.value;
      const oldLayerId = this.value;
      const loadSequence = ++this.layerLoadSequence;

      if (oldLayerId) {
        this.removeTemporaryLayer(oldLayerId);
        geoprocessingSelectionHandler.clearHighlight();
        const map = mapService.get();
        if (map) geoprocessingSelectionHandler.stopSelection(map);
      }

      this.$emit('input', layerId);

      if (!layerId) return;

      let selectedLayer = null;
      for (const cat of this.filteredCategories) {
        const found = cat.layers.find(l => l.id == layerId);
        if (found) {
          selectedLayer = found;
          break;
        }
      }

      if (selectedLayer) {
        const wasAlreadyVisible = LayerService.layerExists(selectedLayer.id);
        try {
          await LayerService.addLayerToMap(selectedLayer, null, false);
          if (!wasAlreadyVisible && LayerService.layerExists(selectedLayer.id)) {
            this.temporaryLayerId = selectedLayer.id;
          }
          // El usuario puede cambiar de opción mientras una capa importada se descarga.
          if (this.layerLoadSequence !== loadSequence) {
            this.removeTemporaryLayer(layerId);
            return;
          }
          this.$nextTick(() => this.onStartDrawing());
        } catch (error) {
          console.error('No se pudo preparar la capa para el geoproceso:', error);
          this.removeTemporaryLayer(layerId);
          this.$emit('input', '');
          this.$store.dispatch('toast/show', {
            message: error.message || 'No se pudo cargar la capa seleccionada',
            type: 'error',
          });
        }
      }
    },
    onStartDrawing() {
      if (!this.value) return;

      let selectedLayer = null;
      for (const cat of this.filteredCategories) {
        const found = cat.layers.find(l => l.id == this.value);
        if (found) {
          selectedLayer = found;
          break;
        }
      }

      if (!selectedLayer) return;

      const map = mapService.get();
      if (!map) return;

      // Force accumulation mode (true) as requested
      geoprocessingSelectionHandler.startSelection(
        map,
        selectedLayer,
        this.tipoInfluencia,
        (features, details) => {
          this.$emit('selection-complete', { features, details });
        },
        true, 
        {
          distancias: this.getOrderedDistances(this.distancias),
          dissolve: this.dissolve
        }
      );
    }
  }
};
</script>

<style scoped>
.geoproceso-selector-capa {
  margin-bottom: 4px;
  padding: 0 5px;
}

.selector-wrapper {
  background: rgba(255, 255, 255, 0.4);
  padding: 6px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.selector-label {
  display: block;
  font-size: 0.8em;
  font-weight: 600;
  color: #444;
  margin-bottom: 3px;
}

.loading-status {
  font-size: 11px;
  color: #666;
  margin-bottom: 3px;
}

.custom-select {
  width: 100%;
  padding: 6px;
  border-radius: 6px;
  border: 1px solid #ccc;
  background-color: white;
  font-size: 0.85em;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.custom-select:focus {
  border-color: #337ab7;
  outline: none;
  box-shadow: 0 0 0 2px rgba(51, 122, 183, 0.1);
}

.selection-hint {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 7px;
  padding: 7px 9px;
  background: rgba(232, 115, 12, 0.1);
  border: 1px solid rgba(232, 115, 12, 0.35);
  border-radius: 6px;
  color: #5c3a1d;
  font-size: 0.78em;
  line-height: 1.3;
}

.selection-hint-icon {
  flex-shrink: 0;
  color: #e8730c;
  font-size: 1.35em;
  font-weight: 700;
  line-height: 1;
}
</style>
